/**
 * Campaign Wizard data source.
 *
 * The auto-filled draft comes from a BE Campaign Wizard run (AAPU-6861): `POST
 * /campaign/wizard/runs` queues it for an intel report, the run is followed over Reverb or by
 * polling `GET /campaign/wizard/runs/{id}` (WizardRunListener), and `POST
 * /campaign/wizard/runs/{id}/campaign` creates the campaign from the confirmed fields, which
 * links it to the intel report the wizard was opened from.
 */

import _ from 'lodash';
import arClient from '../../../util/api_client';
import { isApiSuccess } from '../../../constants/platformStatusCodes';

export const CAMPAIGN_WIZARD_RUNS_ENDPOINT = '/campaign/wizard/runs';
const wizardRunPath = (id) => `${CAMPAIGN_WIZARD_RUNS_ENDPOINT}/${id}`;

const GENERIC_RUN_ERROR = "We couldn't build your campaign draft. Please try again.";
const GENERIC_CREATE_ERROR = "We couldn't create your campaign. Please try again.";
const RATE_LIMIT_ERROR = 'Too many drafts started in a row. Wait a minute, then try again.';

const apiGet = (endpoint) => new Promise((resolve) => arClient.doApiCallGet(endpoint, resolve));

const apiPost = (endpoint, body) =>
  new Promise((resolve) => arClient.doApiCallV2(endpoint, body, resolve));

// Rejection for a failed request or run; `message` is safe to show the user.
const wizardRunError = (message, extra = {}) =>
  Object.assign(new Error(message || GENERIC_RUN_ERROR), extra);

const toRunError = (resp) =>
  wizardRunError(resp?.httpStatus === 429 ? RATE_LIMIT_ERROR : GENERIC_RUN_ERROR, {
    httpStatus: resp?.httpStatus,
  });

/**
 * Run request body. Brand overrides (`brandName`, `brandUrl`, `category`) aren't sent: the
 * brand screen is review-only, so the BE seeds them from the intel report. `objective` is only
 * sent on a re-run after the user picks a different objective.
 * @param {{ intelReportRequestId: number, strategyId?: number|string|null, contextId?: string|null,
 *   objective?: string }} params
 *   - `strategyId` (the report's strategy, AAPU-7258) lets the BE seed the draft from it;
 *   `contextId` (a previous run's) lets a regenerate reuse the brand-site scrape; `objective`
 *   is an OBJECTIVE_OPTIONS value.
 */
export const toWizardRunRequest = ({
  intelReportRequestId,
  strategyId,
  contextId,
  objective,
} = {}) =>
  _.pickBy(
    { intelReportRequestId, strategyId, contextId, objective },
    (value) => value != null && value !== ''
  );

/** Queues a run. Resolves with the run (`status: 'queued'`). */
export const createWizardRun = async (body) => {
  const resp = await apiPost(CAMPAIGN_WIZARD_RUNS_ENDPOINT, body);
  if (isApiSuccess(resp) && resp.data?.id) return resp.data;
  throw toRunError(resp);
};

/**
 * Reads a run: `{ id, status, reasonsStatus, errorMessage, result, … }`. `result` is only set
 * once it's `completed`: `{ contextId, source, fields, reasons, variants, needsReview, … }`.
 * `result.reasons` (`{ [field]: { evidence, basis, source, limitation, wouldRaiseConfidence } }`)
 * is `{}` until `reasonsStatus` is `complete` (see WIZARD_REASONS_STATUS).
 */
export const getWizardRun = async (id) => {
  const resp = await apiGet(wizardRunPath(id));
  if (isApiSuccess(resp) && resp.data) return resp.data;
  throw toRunError(resp);
};

/**
 * Creates the campaign from a completed run's confirmed fields (see `buildCampaignPayload`).
 * Resolves with the new campaign id. Rejects with the BE's reason when it refuses (e.g. a
 * campaign was already created from this run).
 */
export const createCampaignFromWizardRun = async (runId, payload) => {
  const resp = await apiPost(`${wizardRunPath(runId)}/campaign`, payload);
  if (isApiSuccess(resp) && resp.data?.campaignId) return resp.data.campaignId;
  const reason = resp?.httpStatus === 422 && typeof resp.error === 'string' ? resp.error : null;
  throw wizardRunError(reason || GENERIC_CREATE_ERROR, { httpStatus: resp?.httpStatus });
};

/**
 * Stored campaign for editing (the same endpoint the old edit form uses).
 * @returns {Promise<{ campaign: object, campaignDetails: object|null,
 *   campaignBrandDetails: object|null }>} rejects when the campaign can't be loaded.
 */
export const getCampaignForEdit = (campaignId) =>
  new Promise((resolve, reject) => {
    arClient.doApiCall('/campaign/get-overview', { campaignId }, (resp) => {
      if (isApiSuccess(resp) && resp.data?.campaign) {
        const { campaign, campaignDetails, campaignBrandDetails } = resp.data;
        resolve({
          campaign: { ...campaign, id: String(campaign.id) },
          campaignDetails,
          campaignBrandDetails,
        });
      } else {
        reject(resp);
      }
    });
  });
